import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, Plus, List, Cake } from 'lucide-react';
import { useFamily } from '@/hooks/useFamily';
import { useEvents } from '@/hooks/useEvents';
import { usePersonProfiles } from '@/hooks/usePersonProfiles';
import { EventItem } from '@/components/EventItem';
import { EventListItem } from '@/components/events/EventListItem';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { getEventEmoji, filterEventsByType, getEventsForNext12Months, EVENT_FILTER_OPTIONS, type EventFilter } from '@/lib/eventUtils';
import { groupEventsByMonth, groupCurrentMonthByWeek, getEventGroupLabel, getPastEvents, getUpcomingEvents } from '@/lib/dateUtils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { ModernHero } from '@/components/ModernHero';
import calendarHeroImage from '@/assets/calendar-hero.jpg';
import { useModalNavigation } from "@/hooks/useModalNavigation";
import { NoFamilyMessage } from '@/components/NoFamilyMessage';

export default function MobileCalendar() {
  const navigate = useNavigate();
  const { family, familyMembers } = useFamily();
  const { events, loading, refreshEvents, createBirthdayEventsForYear } = useEvents(family?.id);
  const { persons } = usePersonProfiles(family?.id);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('list');
  const [eventFilter, setEventFilter] = useState<EventFilter>('all');
  const [showPast, setShowPast] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(true);
  const { openModal } = useModalNavigation();

  // Manual birthday creation - removed automatic generation
  const handleCreateBirthdayEvents = async () => {
    if (family?.id) {
      try {
        await createBirthdayEventsForYear();
      } catch (error) {
        console.error('Failed to create birthday events:', error);
      }
    }
  };

  if (!family) {
    return (
      <NoFamilyMessage 
        description="Vous devez d'abord créer une famille pour gérer vos événements"
        buttonText="Créer ma famille"
        variant="calendar"
      />
    );
  }

  const filteredEvents = filterEventsByType(events || [], eventFilter);

  const selectedDateEvents = filteredEvents.filter(event => {
    const eventDate = new Date(event.start_date);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  // Utiliser la nouvelle logique pour les événements à venir
  const upcomingEvents = getEventsForNext12Months(events || [], eventFilter);

  const getEventEmojiForDate = (date: Date) => {
    const dayEvents = filteredEvents.filter(event => {
      const eventDate = new Date(event.start_date);
      return eventDate.toDateString() === date.toDateString();
    });
    if (dayEvents.length === 0) return null;
    // Show emoji of the first event type, prioritizing birthdays
    const birthdayEvent = dayEvents.find(e => e.event_type === 'birthday');
    const eventToShow = birthdayEvent || dayEvents[0];
    return getEventEmoji(eventToShow.event_type);
  };

  const heroActions = [
    {
      icon: Calendar,
      label: 'Nouvel événement',
      onClick: () => openModal('createEvent')
    }
  ];

  return (
    <>
      <ModernHero
        title="Calendrier"
        actions={heroActions}
        backgroundImage={calendarHeroImage}
        category="calendar"
        allowImageChange={true}
        height="40vh"
      />

      {/* Content Section with subtle overlap */}
      <div className="relative -mt-24 bg-content-warm rounded-t-3xl min-h-[50vh] px-4 pt-8 pb-16 z-20">{/* ... keep existing code (filters and calendar content) */}
        {/* Filters - integrated style */}
        <div className="mb-6">
          <div className="flex justify-center gap-2 flex-wrap mb-4 max-w-lg mx-auto">
            {EVENT_FILTER_OPTIONS.map((option) => (
              <Button
                key={option.value}
                variant={eventFilter === option.value ? 'calendar' : 'outline'}
                size="sm"
                onClick={() => setEventFilter(option.value)}
                className="rounded-full"
              >
                {option.emoji && `${option.emoji} `}{option.label}
                {eventFilter === option.value && upcomingEvents.length > 0 && (
                  <span className="ml-1 text-xs bg-white/20 rounded-full px-1">
                    {upcomingEvents.length}
                  </span>
                )}
              </Button>
            ))}
          </div>

          {/* View Toggle */}
          <div className="flex justify-center">
            <div className="flex rounded-full bg-muted p-1">
              <Button
                variant={viewMode === 'calendar' ? 'calendar' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className="h-8 w-8 p-0 rounded-full"
              >
                <Calendar className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'calendar' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 w-8 p-0 rounded-full"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <>
            {/* Calendar - cleaner design */}
            <div className="bg-white/50 backdrop-blur-sm rounded-3xl p-4 mb-4 border border-white/20">
              <CalendarComponent
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                className="rounded-md"
                modifiers={{
                  hasEvents: (date) => {
                    const dayEvents = filteredEvents.filter(event => {
                      const eventDate = new Date(event.start_date);
                      return eventDate.toDateString() === date.toDateString();
                    });
                    return dayEvents.length > 0;
                  }
                }}
                modifiersClassNames={{
                  hasEvents: 'relative'
                }}
                components={{
                  DayContent: ({ date }) => {
                    const emoji = getEventEmojiForDate(date);
                    return (
                      <div className="relative">
                        <span>{date.getDate()}</span>
                        {emoji && (
                          <span className="absolute -top-1 -right-1 text-xs">
                            {emoji}
                          </span>
                        )}
                      </div>
                    );
                  }
                }}
              />
            </div>

            {/* Selected date events - simplified */}
            <div className="bg-white/50 backdrop-blur-sm rounded-3xl p-4 border border-white/20">
              <h3 className="font-semibold mb-3 text-foreground">
                {selectedDate.toLocaleDateString('fr-FR', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </h3>
              {selectedDateEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">Aucun événement ce jour</p>
              ) : (
                <div className="space-y-3">
                   {selectedDateEvents.map((event) => (
                     <EventItem 
                       key={event.id} 
                       event={event} 
                       familyMembers={familyMembers}
                       compact 
                       onDeleted={() => refreshEvents()}
                     />
                   ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* List view - improved organization */
          <div className="space-y-6">
            {loading ? (
              [...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse bg-white/50 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              ))
            ) : (() => {
              const eventsToShow = eventFilter === 'all' ? events : upcomingEvents;
              
              if (eventsToShow.length === 0) {
                return (
                  <div className="text-center py-8">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">
                      {eventFilter === 'all' 
                        ? 'Aucun événement trouvé' 
                        : `Aucun événement de type "${EVENT_FILTER_OPTIONS.find(o => o.value === eventFilter)?.label}" dans les 12 prochains mois`
                      }
                    </p>
                    <Button onClick={() => openModal('createEvent')} variant="calendar" className="rounded-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Créer un événement
                    </Button>
                  </div>
                );
              }

              // Séparer les événements passés et à venir
              const upcomingEventsFiltered = getUpcomingEvents(eventsToShow);
              const pastEventsFiltered = getPastEvents(eventsToShow);

              return (
                <div className="space-y-6">
                  {/* Section des événements à venir */}
                  {upcomingEventsFiltered.length > 0 ? (
                    (() => {
                      // Grouper les événements à venir par mois
                      const monthGroups = groupEventsByMonth(upcomingEventsFiltered);
                      const today = new Date();
                      const currentMonthKey = format(today, 'yyyy-MM');
                      const isCurrentMonth = (monthKey: string) => monthKey === currentMonthKey;

                      return Array.from(monthGroups.entries())
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([monthKey, monthEvents]) => {
                          const isCurrent = isCurrentMonth(monthKey);
                          
                          if (isCurrent) {
                            // Pour le mois courant, grouper par semaine
                            const weekGroups = groupCurrentMonthByWeek(monthEvents);
                            
                            return (
                              <div key={monthKey} className="space-y-4">
                                {/* Cette semaine */}
                                {weekGroups.thisWeek.length > 0 && (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <h3 className="text-lg font-semibold text-foreground">Cette semaine</h3>
                                      <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                        {weekGroups.thisWeek.length}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      {weekGroups.thisWeek.map((event) => (
                                         <EventListItem
                                           key={event.id}
                                           event={event}
                                           onEventClick={() => navigate(`/event/${event.id}`)}
                                         />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* La semaine prochaine */}
                                {weekGroups.nextWeek.length > 0 && (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <h3 className="text-lg font-semibold text-foreground">La semaine prochaine</h3>
                                      <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                        {weekGroups.nextWeek.length}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      {weekGroups.nextWeek.map((event) => (
                                         <EventListItem
                                           key={event.id}
                                           event={event}
                                           onEventClick={() => navigate(`/event/${event.id}`)}
                                         />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {/* Plus tard ce mois-ci */}
                                {weekGroups.laterThisMonth.length > 0 && (
                                  <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                      <h3 className="text-lg font-semibold text-foreground">Plus tard ce mois-ci</h3>
                                      <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                        {weekGroups.laterThisMonth.length}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      {weekGroups.laterThisMonth.map((event) => (
                                         <EventListItem
                                           key={event.id}
                                           event={event}
                                           onEventClick={() => navigate(`/event/${event.id}`)}
                                         />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          } else {
                            // Pour les autres mois, afficher tous les événements directement (sans accordéon)
                            const monthLabel = getEventGroupLabel(monthKey, false);
                            
                            return (
                              <div key={monthKey} className="space-y-3">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-semibold text-foreground">{monthLabel}</h3>
                                  <div className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full">
                                    {monthEvents.length}
                                  </div>
                                </div>
                                <div className="space-y-2">
                                  {monthEvents
                                    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
                                    .map((event) => (
                                       <EventListItem
                                         key={event.id}
                                         event={event}
                                         onEventClick={() => navigate(`/event/${event.id}`)}
                                       />
                                    ))
                                  }
                                </div>
                              </div>
                            );
                          }
                        });
                    })()
                  ) : (
                    <div className="text-center py-8">
                      <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground mb-4">
                        {eventFilter === 'all' 
                          ? 'Aucun événement à venir' 
                          : `Aucun événement de type "${EVENT_FILTER_OPTIONS.find(o => o.value === eventFilter)?.label}" à venir`
                        }
                      </p>
                      <Button onClick={() => openModal('createEvent')} variant="calendar" className="rounded-full">
                        <Plus className="h-4 w-4 mr-2" />
                        Créer un événement
                      </Button>
                    </div>
                  )}

                  {/* Section des événements passés (accordéon) */}
                  {pastEventsFiltered.length > 0 && (
                    <Collapsible defaultOpen={false}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 border-t border-border/30 transition-colors group">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xs font-normal text-muted-foreground">Événements passés</h3>
                          <div className="px-1.5 py-0.5 bg-muted/60 text-muted-foreground text-xs rounded-full">
                            {pastEventsFiltered.length}
                          </div>
                        </div>
                        <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-3">
                        <div className="space-y-2">
                          {pastEventsFiltered.map((event) => (
                             <EventListItem
                               key={event.id}
                               event={event}
                               onEventClick={() => navigate(`/event/${event.id}`)}
                             />
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </>
  );
}